import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { useEffect } from 'react';

// Layout
import { AdminLayout } from './components/layout/AdminLayout';

// Pages
import { LoginPage } from './routes/login';
import { DashboardPage } from './routes/index';
import { ClientsPage } from './routes/clients/index';
import { ClientDetailPage } from './routes/clients/[id]';
import { NewClientPage } from './routes/clients/new';
import { ProjectsPage } from './routes/projects/index';
import { ProjectDetailPage } from './routes/projects/[id]';
import { NewProjectPage } from './routes/projects/new';
import { RequestsPage } from './routes/requests/index';
import { RequestDetailPage } from './routes/requests/[id]';
import { NewRequestPage } from './routes/requests/new';
import { BundlesPage } from './routes/bundles/index';
import { BundleDetailPage } from './routes/bundles/[id]';
import { NewBundlePage } from './routes/bundles/new';
import { WorkItemDetailPage } from './routes/work-items/[id]';
import { SettingsPage } from './routes/settings/index';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Loading screen with animation
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-accent/20 rounded-full" />
        <div className="absolute inset-0 w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const { theme } = useThemeStore();
  const { checkAuth } = useAuthStore();

  // Apply theme class to document
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/new" element={<NewClientPage />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/new" element={<NewProjectPage />} />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route path="requests/new" element={<NewRequestPage />} />
          <Route path="requests/:id" element={<RequestDetailPage />} />
          <Route path="bundles" element={<BundlesPage />} />
          <Route path="bundles/new" element={<NewBundlePage />} />
          <Route path="bundles/:id" element={<BundleDetailPage />} />
          <Route path="work-items/:id" element={<WorkItemDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}
